/**
 * Material Readiness Resolver — Procurement Hardening Phase 3
 * ─────────────────────────────────────────────────────────────────────
 * Canonical, deterministic install-readiness truth for materials.
 *
 * This is the procurement equivalent of `resolvePermitOperationalSignals()`
 * but focused specifically on the question: "Is this job's material truth
 * safe to schedule / install against?"
 *
 * Distinct from `resolveProcurementSignals()` (Phase 1B):
 *   - Phase 1B = multi-faceted procurement health (ETA / receipt / drift / sub).
 *   - Phase 3  = single deterministic install-readiness verdict.
 *
 * Both are pure derivation. Phase 3 layers on top of Phase 1B output to
 * avoid re-interpreting the same source-of-truth fields.
 *
 * Invariants (LOCKED):
 *   1. PURE derivation — no mutation, no persistence, no queue events,
 *      no notifications, no scheduling decisions, no automation.
 *   2. NO AppDataContext / React imports. Inputs explicit.
 *   3. NO writers. NO action queue.
 *   4. Deterministic precedence — see READINESS PRIORITY below.
 *   5. Reuses Phase 0A truth (etaConfirmed/vendorAckAt/allocationBlocked)
 *      via Phase 1B signal output. Never re-derives from raw fields where
 *      Phase 1B already does so.
 */

import type { Job, MaterialLineItem } from "@/data/types";
import type { PurchaseOrder, FieldMaterialFeedbackEvent, SubstitutionRequest } from "@/data/VendorPOContext";
import type { OrderingLineItem } from "@/data/orderingPacket/types";
import type {
  ReceivingRecord,
  JobAllocation,
} from "@/data/WarehouseInventoryContext";
import type { JobBomReconciliation } from "@/data/materialsReconciliation";
import {
  resolveProcurementSignals,
  type ProcurementOperationalSignals,
} from "@/data/procurement/procurementOperationalSignals";

/* ── Public types ────────────────────────────────────────────────── */

export type MaterialReadinessSeverity = "neutral" | "info" | "warn" | "blocker";

export type MaterialReadinessState =
  | "not_required"
  | "not_started"
  | "ordered"
  | "awaiting_vendor_confirmation"
  | "awaiting_receipt"
  | "partial_ready"
  | "blocked_field_material_issue"
  | "blocked_discrepancy"
  | "blocked_missing_critical"
  | "blocked_substitution"
  | "blocked_reconciliation"
  | "ready";

export interface MaterialReadinessInput {
  job: Job | null | undefined;
  purchaseOrders?: PurchaseOrder[];
  orderingPackets?: OrderingLineItem[];
  receivingRecords?: ReceivingRecord[];
  jobAllocations?: JobAllocation[];
  reconciliation?: JobBomReconciliation | null;
  /** Phase 4 — append-only field material feedback for THIS job. */
  fieldFeedback?: FieldMaterialFeedbackEvent[];
  /** Phase 5 — append-only substitution governance requests for THIS job. */
  substitutionRequests?: SubstitutionRequest[];
  /** Optional pre-computed Phase 1B signals. Resolver computes them
   *  internally if omitted, keeping a single source of truth. */
  procurementSignals?: ProcurementOperationalSignals;
}

export interface MaterialReadinessVerdict {
  readinessState: MaterialReadinessState;
  installReady: boolean;
  blocker: boolean;
  blockerReason?: string;
  severity: MaterialReadinessSeverity;

  percentComplete: number;
  criticalItemsTotal: number;
  criticalItemsReady: number;
  missingCriticalItems: string[];

  /** Phase 4 — counts of unresolved field feedback by severity. */
  unresolvedFieldBlockers: number;
  unresolvedFieldWarns: number;

  /** Phase 5 — substitution governance summary for THIS job. */
  substitutionPendingCount: number;
  substitutionApprovedCompatibleCount: number;
  substitutionConditionalCount: number;
  substitutionIncompatibleCount: number;
  substitutionRejectedOpenCount: number;

  summaryLabel: string;
  readinessLabel: string;
  schedulingLabel: string;
  installOpsLabel: string;
}

/* ── Helpers ─────────────────────────────────────────────────────── */

const NOT_REQUIRED: MaterialReadinessVerdict = {
  readinessState: "not_required",
  installReady: false, // not_required is gating-neutral; surfaces should hide chips
  blocker: false,
  severity: "neutral",
  percentComplete: 0,
  criticalItemsTotal: 0,
  criticalItemsReady: 0,
  missingCriticalItems: [],
  unresolvedFieldBlockers: 0,
  unresolvedFieldWarns: 0,
  substitutionPendingCount: 0,
  substitutionApprovedCompatibleCount: 0,
  substitutionConditionalCount: 0,
  substitutionIncompatibleCount: 0,
  substitutionRejectedOpenCount: 0,
  summaryLabel: "Materials not required",
  readinessLabel: "Materials not required",
  schedulingLabel: "Materials not required",
  installOpsLabel: "Materials not required",
};

function countCriticalLines(
  pos: PurchaseOrder[],
  packets: OrderingLineItem[],
  reconciliation: JobBomReconciliation | null,
): { total: number; readyNames: Set<string>; allNames: Set<string> } {
  const allNames = new Set<string>();
  const readyNames = new Set<string>();

  // Mirror Phase 1B's "critical line" definition for cross-layer parity.
  const CRITICAL_HINTS = [
    "panel", "base", "door", "glass", "tub", "pan", "valve", "diverter",
  ];
  const CRITICAL_CATEGORIES = new Set([
    "wall_panel", "wall panel", "panels",
    "door", "doors", "shower_door", "glass",
    "tub", "base", "shower_base", "pan",
    "fixture_primary",
  ]);

  function isCritical(sku: string, name: string): boolean {
    const m = packets.find(
      (p) => p.actualSku === sku || p.projectedSku === sku,
    );
    if (m?.category && CRITICAL_CATEGORIES.has(m.category.toLowerCase())) return true;
    const n = (name || "").toLowerCase();
    return CRITICAL_HINTS.some((h) => n.includes(h));
  }

  for (const po of pos) {
    for (const item of po.items) {
      if (!isCritical(item.sku, item.productName)) continue;
      allNames.add(item.productName);
    }
  }
  if (reconciliation) {
    for (const row of reconciliation.rows) {
      if (!isCritical(row.sku, row.itemName)) continue;
      allNames.add(row.itemName);
    }
  }
  if (reconciliation) {
    for (const row of reconciliation.rows) {
      if (!allNames.has(row.itemName)) continue;
      const need = row.specQty || row.displayQty || 0;
      if (need <= 0) continue;
      const placed = row.quantityStaged + row.quantityInstalled + row.quantityAllocated;
      if (placed >= need && row.status !== "off-spec") {
        readyNames.add(row.itemName);
      }
    }
  }

  return { total: allNames.size, readyNames, allNames };
}

function pctComplete(
  reconciliation: JobBomReconciliation | null,
  receiving: ReceivingRecord[],
): number {
  if (reconciliation && reconciliation.counts.total > 0) {
    const denom = reconciliation.counts.total;
    const num = reconciliation.counts.aligned;
    return Math.round((num / denom) * 100);
  }
  if (receiving.length > 0) {
    const full = receiving.filter((r) => r.status === "Fully Received").length;
    return Math.round((full / receiving.length) * 100);
  }
  return 0;
}

function hasMaterialFootprint(
  materialsList: MaterialLineItem[] | undefined,
  pos: PurchaseOrder[],
  packets: OrderingLineItem[],
  recon: JobBomReconciliation | null,
): boolean {
  if (materialsList && materialsList.length > 0) return true;
  if (pos.length > 0) return true;
  if (packets.length > 0) return true;
  if (recon && recon.rows.length > 0) return true;
  return false;
}

/* ── Resolver ────────────────────────────────────────────────────── */

export function resolveMaterialReadiness(
  input: MaterialReadinessInput,
): MaterialReadinessVerdict {
  const { job } = input;
  if (!job) return NOT_REQUIRED;

  const pos = input.purchaseOrders ?? [];
  const packets = input.orderingPackets ?? [];
  const receiving = input.receivingRecords ?? [];
  const allocations = input.jobAllocations ?? [];
  const reconciliation = input.reconciliation ?? null;
  const materialsList = job.materialsList;

  if (!hasMaterialFootprint(materialsList, pos, packets, reconciliation)) {
    return NOT_REQUIRED;
  }

  const sig: ProcurementOperationalSignals =
    input.procurementSignals ??
    resolveProcurementSignals({
      job,
      purchaseOrders: pos,
      orderingPackets: packets,
      receivingRecords: receiving,
      jobAllocations: allocations,
      materialReconciliation: reconciliation,
    });

  const critical = countCriticalLines(pos, packets, reconciliation);
  const criticalItemsReady = critical.readyNames.size;
  const criticalItemsTotal = critical.total;
  const missingCriticalItems = sig.missingCriticalItems;
  const percentComplete = pctComplete(reconciliation, receiving);

  // Phase 4 — field feedback (append-only, unresolved-only gates readiness)
  const fieldFeedback = (input.fieldFeedback ?? []).filter((e) => !e.resolved);
  const unresolvedFieldBlockers = fieldFeedback.filter((e) => e.severity === "blocker").length;
  const unresolvedFieldWarns = fieldFeedback.filter((e) => e.severity === "warn").length;
  const topFieldBlocker = fieldFeedback.find((e) => e.severity === "blocker");

  // Phase 5 — substitution governance (deterministic precedence)
  const subs = input.substitutionRequests ?? [];
  const subsPending = subs.filter((r) => r.status === "pending_review");
  const subsApprovedCompatible = subs.filter(
    (r) => r.status === "approved" && r.compatibilityAssessment === "compatible",
  );
  const subsApprovedConditional = subs.filter(
    (r) => r.status === "approved" && r.compatibilityAssessment === "conditional",
  );
  const subsIncompatibleOpen = subs.filter(
    (r) => r.compatibilityAssessment === "incompatible" && r.status !== "superseded",
  );
  // Rejected = remains blocked until alternate path exists. Group by
  // originalMaterialReference|originalDescription; if no approved-compatible
  // peer exists, the rejection still gates readiness.
  const subsRejectedOpen = subs.filter((r) => {
    if (r.status !== "rejected") return false;
    const key = r.originalMaterialReference || r.originalDescription;
    const replaced = subs.some(
      (other) =>
        other.id !== r.id &&
        other.status === "approved" &&
        other.compatibilityAssessment === "compatible" &&
        (other.originalMaterialReference || other.originalDescription) === key,
    );
    return !replaced;
  });
  const topSubBlocker =
    subsIncompatibleOpen[0] ?? subsPending[0] ?? subsRejectedOpen[0];
  const topSubConditional = subsApprovedConditional[0];

  // ── Deterministic precedence (highest first) ────────────────────
  let state: MaterialReadinessState;
  let severity: MaterialReadinessSeverity;
  let installReady = false;
  let blocker = false;
  let blockerReason: string | undefined;
  let summary: string;

  // 0. Phase 4 — unresolved blocker-level field feedback (HIGHEST precedence)
  if (topFieldBlocker) {
    state = "blocked_field_material_issue";
    severity = "blocker";
    blocker = true;
    blockerReason = `Field reported · ${formatIssueType(topFieldBlocker.issueType)} — ${topFieldBlocker.description}`;
    summary = `Field Material Issue · ${formatIssueType(topFieldBlocker.issueType)}`;
  }
  // 1. Discrepancy blocking allocation
  else if (sig.allocationBlocked) {
    state = "blocked_discrepancy";
    severity = "blocker";
    blocker = true;
    blockerReason = sig.allocationBlockedReason || "Allocation blocked due to receiving discrepancy";
    summary = `Blocked · ${blockerReason}`;
  }
  // 2. Critical missing items
  else if (missingCriticalItems.length > 0) {
    state = "blocked_missing_critical";
    severity = "blocker";
    blocker = true;
    blockerReason = `Critical materials missing · ${missingCriticalItems[0]}`;
    summary = `Critical Materials Missing · ${missingCriticalItems.length}`;
  }
  // 3. Substitution governance — pending/incompatible/rejected-without-replacement
  //    OR legacy line-item substitution risk (packet-level fallback).
  else if (topSubBlocker) {
    state = "blocked_substitution";
    severity = "blocker";
    blocker = true;
    const reasonLabel =
      topSubBlocker.status === "rejected"
        ? "rejected — no approved replacement"
        : topSubBlocker.compatibilityAssessment === "incompatible"
          ? "compatibility incompatible"
          : "compatibility unverified";
    blockerReason = `Substitution governance · ${reasonLabel} (${topSubBlocker.proposedDescription})`;
    summary = `Substitution Pending Review`;
  }
  else if (sig.substitutionRisk) {
    // Legacy packet-level substitution (no governance request yet) — blocker.
    state = "blocked_substitution";
    severity = "blocker";
    blocker = true;
    blockerReason = `Substitution pending review · ${sig.substitutionReason ?? "compatibility unverified"}`;
    summary = `Substitution Pending Review`;
  }
  // 4. Reconciliation drift
  else if (sig.reconciliationDrift) {
    state = "blocked_reconciliation";
    // Spec drift = blocker; qty-only drift = warn (Phase 1B already separates).
    const isSpecDrift = !!reconciliation && reconciliation.counts.offSpec > 0;
    severity = isSpecDrift ? "blocker" : "warn";
    blocker = isSpecDrift;
    blockerReason = isSpecDrift
      ? `Material spec drift · ${sig.reconciliationSummary ?? ""}`.trim()
      : undefined;
    summary = isSpecDrift
      ? `Material Spec Drift`
      : `Material Reconciliation Drift · ${sig.reconciliationSummary ?? ""}`.trim();
  }
  // 5. Vendor ETA not confirmed
  else if (pos.length > 0 && sig.etaConfidence !== "vendor_confirmed" && !sig.partialReceipt && !receivingFullyClean(receiving)) {
    state = "awaiting_vendor_confirmation";
    severity = "warn";
    summary = "Awaiting Vendor ETA Confirmation";
  }
  // 6. Ordered awaiting receipt
  else if (pos.length > 0 && receiving.length === 0) {
    state = "ordered";
    severity = "info";
    summary = "Ordered · Awaiting Receipt";
  }
  // 7. Partial receipt
  else if (sig.partialReceipt) {
    // Critical coverage met → partial_ready (warn). Otherwise treat as awaiting_receipt.
    if (criticalItemsTotal > 0 && criticalItemsReady >= criticalItemsTotal) {
      state = "partial_ready";
      severity = "warn";
      summary = "Partial Materials Ready · non-critical pending";
    } else {
      state = "awaiting_receipt";
      severity = "warn";
      summary = "Awaiting Material Receipt";
    }
  }
  // 8. Fully ready
  else if (receivingFullyClean(receiving) && !sig.reconciliationDrift) {
    state = "ready";
    severity = "info";
    installReady = true;
    summary = "Materials Ready";
  }
  // Fallback — POs exist but state ambiguous
  else if (pos.length > 0) {
    state = "ordered";
    severity = "info";
    summary = "Ordered";
  } else {
    state = "not_started";
    severity = "warn";
    summary = "Procurement Not Started";
  }

  // Phase 4 — warn-level field feedback (no blocker fired): degrade severity
  // and label, but never hard-block. installReady is forced false.
  if (!blocker && unresolvedFieldWarns > 0) {
    if (severity === "info") {
      severity = "warn";
    }
    installReady = false;
    summary = `${summary} · Field warning${unresolvedFieldWarns === 1 ? "" : "s"} (${unresolvedFieldWarns})`;
  }

  // Phase 5 — approved+conditional substitution degrades to warn (no block).
  // Approved+compatible never blocks, never warns — silent on this dimension.
  if (!blocker && topSubConditional) {
    if (severity === "info") severity = "warn";
    installReady = false;
    summary = `${summary} · Conditional substitute (${topSubConditional.proposedDescription})`;
  }

  // Lens labels
  const readinessLabel =
    blocker
      ? `Material Blocker · ${blockerReason}`
      : state === "ready"
        ? "Materials Ready"
        : state === "partial_ready"
          ? "Partial Materials Ready"
          : state === "awaiting_vendor_confirmation"
            ? "Vendor ETA Unconfirmed"
            : state === "awaiting_receipt"
              ? "Awaiting Receipt"
              : summary;

  const schedulingLabel =
    state === "blocked_field_material_issue"
      ? `Material Hold · Field issue (${formatIssueType(topFieldBlocker!.issueType)})`
      : state === "blocked_discrepancy"
        ? "Material Hold · Allocation blocked"
        : state === "blocked_missing_critical"
          ? "Material Hold · Critical items missing"
          : state === "blocked_substitution"
            ? "Material Hold · Substitution review"
            : state === "blocked_reconciliation"
              ? blocker ? "Material Hold · Spec drift" : "Reconciliation drift"
              : state === "awaiting_vendor_confirmation"
                ? "Awaiting Vendor"
                : state === "partial_ready"
                  ? "Partial"
                  : state === "ready"
                    ? "Ready"
                    : summary;

  const installOpsLabel =
    blocker
      ? `Install Blocker · ${blockerReason}`
      : state === "ready"
        ? "Materials Ready"
        : state === "partial_ready"
          ? "Partial Ready"
          : severity === "warn"
            ? summary
            : summary;

  return {
    readinessState: state,
    installReady,
    blocker,
    blockerReason,
    severity,
    percentComplete,
    criticalItemsTotal,
    criticalItemsReady,
    missingCriticalItems,
    unresolvedFieldBlockers,
    unresolvedFieldWarns,
    substitutionPendingCount: subsPending.length,
    substitutionApprovedCompatibleCount: subsApprovedCompatible.length,
    substitutionConditionalCount: subsApprovedConditional.length,
    substitutionIncompatibleCount: subsIncompatibleOpen.length,
    substitutionRejectedOpenCount: subsRejectedOpen.length,
    summaryLabel: summary,
    readinessLabel,
    schedulingLabel,
    installOpsLabel,
  };
}

/* ── Phase 4 helpers ─────────────────────────────────────────────── */

const ISSUE_TYPE_LABELS: Record<string, string> = {
  missing_material: "Missing material",
  wrong_material: "Wrong material",
  damaged_material: "Damaged material",
  incomplete_kit: "Incomplete kit",
  quantity_shortfall: "Quantity shortfall",
  spec_incompatibility: "Spec incompatibility",
  substitution_problem: "Substitution problem",
  other: "Other field issue",
};

function formatIssueType(t: string): string {
  return ISSUE_TYPE_LABELS[t] ?? t;
}

function receivingFullyClean(receiving: ReceivingRecord[]): boolean {
  if (receiving.length === 0) return false;
  return receiving.every(
    (r) => r.status === "Fully Received" && !r.allocationBlocked,
  );
}
